import { useEffect, useState, Fragment } from 'react'
import { Link, useParams } from 'react-router-dom'
import AdminShell from './AdminShell'
import { tournamentApi } from '../../services/api'

const money = (n) => `$${Number(n || 0).toFixed(2)}`
const pctStr = (f) => `${(Number(f || 0) * 100).toFixed(2)}%`

/**
 * Super-admin payouts — Phase 11.
 * Two views: list (per-reseller running balance) + detail (per-booking
 * breakdown + mark-paid + CSV export).
 */
export function PayoutsList() {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)

  useEffect(() => {
    tournamentApi.get('/admin/reseller-payouts')
      .then(r => setData(r.data?.resellers || []))
      .catch(e => setErr(e?.response?.data?.message || e.message))
      .finally(() => setLoading(false))
  }, [])

  return (
    <AdminShell title="Reseller payouts">
      <p style={{ color: '#64748b', marginTop: 0, marginBottom: 24, fontSize: 14, maxWidth: 720 }}>
        Per-reseller running balance for Mode A bookings (InteGolf collected, owe the reseller). Click a row to see the per-booking breakdown and mark a payout as sent.
      </p>
      {err && <div style={errorBox}>{err}</div>}
      {loading
        ? <div style={empty}>Loading…</div>
        : (
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
            <table style={tbl}>
              <thead>
                <tr style={tr}>
                  <th style={th}>Reseller</th>
                  <th style={th}>Currency</th>
                  <th style={th}>Unpaid bookings</th>
                  <th style={th}>Unpaid total</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {data.map(r => (
                  <tr key={r.ResellerAccountId} style={tr}>
                    <td style={td}>
                      <strong>{r.Name}</strong>
                      <div style={{ fontSize: 12, color: '#64748b' }}>{r.Slug}</div>
                    </td>
                    <td style={tdMono}>{r.Currency}</td>
                    <td style={tdMono}>{r.UnpaidBookingCount}</td>
                    <td style={{ ...tdMono, fontWeight: 700 }}>${Number(r.UnpaidAmount).toFixed(2)}</td>
                    <td style={td}>
                      <Link to={`/admin/reseller-payouts/${r.ResellerAccountId}`} style={{ ...secondaryBtn, textDecoration: 'none', display: 'inline-block' }}>
                        View detail →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }
    </AdminShell>
  )
}

export function PayoutsDetail() {
  const { resellerId } = useParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [reference, setReference] = useState('')
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [openId, setOpenId] = useState(null)
  const [detail, setDetail] = useState({})       // { [bookingId]: response }
  const [detailErr, setDetailErr] = useState({}) // { [bookingId]: message }

  function reload() {
    setLoading(true); setErr(null)
    tournamentApi.get(`/admin/reseller-payouts/${resellerId}`)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.message || e.message))
      .finally(() => setLoading(false))
  }
  useEffect(reload, [resellerId])

  function toggleRow(bookingId) {
    if (openId === bookingId) { setOpenId(null); return }
    setOpenId(bookingId)
    if (!detail[bookingId]) {
      tournamentApi.get(`/admin/reseller-payouts/${resellerId}/booking/${bookingId}`)
        .then(r => setDetail(d => ({ ...d, [bookingId]: r.data })))
        .catch(e => setDetailErr(d => ({ ...d, [bookingId]: e?.response?.data?.message || e.message })))
    }
  }

  async function markPaid() {
    if (!confirm(`Mark all ${data.unpaid.length} bookings as paid out (${data.reseller.Currency} $${data.unpaidTotal.toFixed(2)})?`)) return
    setSubmitting(true)
    try {
      const r = await tournamentApi.post(`/admin/reseller-payouts/${resellerId}/mark-paid`, { reference, note })
      alert(`Marked ${r.data.paidCount} bookings as paid for $${r.data.amountTotal}.`)
      setReference(''); setNote('')
      reload()
    } catch (e) { alert(e?.response?.data?.message || e.message) }
    finally { setSubmitting(false) }
  }

  if (loading) return <AdminShell title="Loading…"><div /></AdminShell>
  if (err || !data) return <AdminShell title="Reseller payouts"><div style={errorBox}>{err || 'Not found'}</div></AdminShell>

  return (
    <AdminShell title={`${data.reseller.Name} — Payouts`}>
      <Link to="/admin/reseller-payouts" style={{ color: '#475569', textDecoration: 'none', fontSize: 13 }}>← All resellers</Link>

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 20, marginTop: 16, marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Unpaid balance: <span style={{ color: '#16a34a' }}>${data.unpaidTotal.toFixed(2)} {data.reseller.Currency}</span></h3>
        <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>{data.unpaid.length} booking{data.unpaid.length !== 1 ? 's' : ''} owed. Send via Scotiabank, then mark paid below.</p>
        <div style={{ marginTop: 16, display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <Field label="EFT reference" style={{ flex: 1 }}><input value={reference} onChange={e => setReference(e.target.value)} style={inp} placeholder="Scotiabank confirmation #" /></Field>
          <Field label="Note (optional)" style={{ flex: 2 }}><input value={note} onChange={e => setNote(e.target.value)} style={inp} placeholder="e.g. May 2026 batch" /></Field>
          <button onClick={markPaid} disabled={submitting || data.unpaid.length === 0} style={{ ...primaryBtn, padding: '10px 20px', opacity: (submitting || data.unpaid.length === 0) ? 0.5 : 1 }}>
            {submitting ? 'Recording…' : 'Mark all as paid'}
          </button>
          <a href={`/api/admin/reseller-payouts/${resellerId}/csv`} target="_blank" rel="noopener" style={{ ...secondaryBtn, padding: '10px 16px', textDecoration: 'none', display: 'inline-block' }}>↓ CSV</a>
        </div>
      </div>

      <h3 style={{ margin: '24px 0 12px', fontSize: 15 }}>Unpaid bookings</h3>
      <p style={{ fontSize: 12, color: '#94a3b8', margin: '0 0 12px' }}>Click a booking to see the full payout breakdown and confirm the math.</p>
      {data.unpaid.length === 0
        ? <div style={empty}>Nothing owed. Balance is zero.</div>
        : (
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
            <table style={tbl}>
              <thead>
                <tr style={tr}>
                  <th style={th}></th>
                  <th style={th}>#</th>
                  <th style={th}>Customer</th>
                  <th style={th}>Dates</th>
                  <th style={th}>Total paid</th>
                  <th style={th}>Refunded</th>
                  <th style={th}>Net</th>
                  <th style={th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.unpaid.map(b => {
                  const open = openId === b.ID_NO
                  return (
                    <Fragment key={b.ID_NO}>
                      <tr style={{ ...tr, cursor: 'pointer', background: open ? '#f8fafc' : undefined }} onClick={() => toggleRow(b.ID_NO)}>
                        <td style={{ ...tdMono, color: '#94a3b8', width: 24 }}>{open ? '▾' : '▸'}</td>
                        <td style={tdMono}>#{b.ID_NO}</td>
                        <td style={td}><strong>{b.GolferName}</strong><div style={{ fontSize: 12, color: '#64748b' }}>{b.GolferEmail}</div></td>
                        <td style={tdMono}>{b.CheckInDate} → {b.CheckOutDate}</td>
                        <td style={tdMono}>${Number(b.TotalCustomerPaid || 0).toFixed(2)}</td>
                        <td style={tdMono}>{b.RefundedAmount ? `$${Number(b.RefundedAmount).toFixed(2)}` : '—'}</td>
                        <td style={{ ...tdMono, fontWeight: 700 }}>${(Number(b.NetToReseller || b.TotalCustomerPaid || 0) - Number(b.RefundedAmount || 0)).toFixed(2)}</td>
                        <td style={td}>{b.Status}</td>
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={8} style={{ padding: 0, background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                            {detailErr[b.ID_NO]
                              ? <div style={{ ...errorBox, margin: 16 }}>{detailErr[b.ID_NO]}</div>
                              : !detail[b.ID_NO]
                                ? <div style={{ padding: 24, color: '#64748b', fontSize: 13 }}>Loading breakdown…</div>
                                : <BookingBreakdown d={detail[b.ID_NO]} />}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )
      }

      {data.history.length > 0 && (
        <>
          <h3 style={{ margin: '24px 0 12px', fontSize: 15 }}>Payout history</h3>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
            <table style={tbl}>
              <thead>
                <tr style={tr}>
                  <th style={th}>Date</th>
                  <th style={th}>Amount</th>
                  <th style={th}>Bookings covered</th>
                  <th style={th}>Reference</th>
                  <th style={th}>Note</th>
                </tr>
              </thead>
              <tbody>
                {data.history.map(p => (
                  <tr key={p.ID_NO} style={tr}>
                    <td style={tdMono}>{new Date(p.PaidAt).toLocaleDateString()}</td>
                    <td style={{ ...tdMono, fontWeight: 700 }}>{p.Currency} ${Number(p.AmountTotal).toFixed(2)}</td>
                    <td style={tdMono}>{(JSON.parse(p.CoveredBookingsJson || '[]')).length}</td>
                    <td style={tdMono}>{p.Reference || '—'}</td>
                    <td style={td}>{p.Note || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </AdminShell>
  )
}

function BookingBreakdown({ d }) {
  const cur = d.booking?.Currency || 'CAD'
  const netMatch = d.net?.reconciles
  const gateway = d.fees?.gatewayFee || 0
  const taxAmt = d.tax?.amount || 0
  const taxWithheld = d.tax?.withheldFromPayout || 0
  const markupAdj = (d.revenue?.markupTaxAdjustments || 0) - taxAmt

  return (
    <div style={{ padding: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
      {/* ── Revenue (what the customer paid) ── */}
      <div style={panel}>
        <div style={panelHead}>What the customer paid</div>
        <table style={miniTbl}>
          <tbody>
            {d.revenue.items.map((it, i) => (
              <tr key={i}><td style={miniLabel}>{it.label}</td><td style={miniAmt}>{money(it.amount)}</td></tr>
            ))}
            <tr style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ ...miniLabel, color: '#475569' }}>Line-item subtotal</td>
              <td style={miniAmt}>{money(d.revenue.itemsTotal)}</td>
            </tr>
            {Math.abs(markupAdj) > 0.005 && (
              <tr><td style={{ ...miniLabel, color: '#94a3b8' }}>Reseller markup &amp; adjustments</td><td style={{ ...miniAmt, color: '#94a3b8' }}>{money(markupAdj)}</td></tr>
            )}
            {taxAmt > 0.005 && (
              <tr><td style={{ ...miniLabel, color: '#475569' }}>Tax ({pctStr(d.tax.rate)}{d.tax.lines?.length ? ` · ${d.tax.lines.map(l => l.name).join(' + ')}` : ''})</td><td style={miniAmt}>{money(taxAmt)}</td></tr>
            )}
            <tr style={{ borderTop: '2px solid #cbd5e1' }}>
              <td style={{ ...miniLabel, fontWeight: 700 }}>Total customer paid</td>
              <td style={{ ...miniAmt, fontWeight: 700 }}>{money(d.revenue.total)} {cur}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ── Payout math ── */}
      <div style={panel}>
        <div style={panelHead}>How the payout is calculated</div>
        <table style={miniTbl}>
          <tbody>
            <tr><td style={miniLabel}>Total customer paid</td><td style={miniAmt}>{money(d.revenue.total)}</td></tr>
            <tr><td style={miniLabel}>− Hotel cost (paid to supplier)</td><td style={{ ...miniAmt, color: '#b91c1c' }}>−{money(d.costs.hotelCost)}</td></tr>
            <tr><td style={miniLabel}>− Golf cost (paid to courses)</td><td style={{ ...miniAmt, color: '#b91c1c' }}>−{money(d.costs.golfCost)}</td></tr>
            {d.fees.integolfMarkup > 0 && (
              <tr><td style={miniLabel}>− InteGolf markup</td><td style={{ ...miniAmt, color: '#b91c1c' }}>−{money(d.fees.integolfMarkup)}</td></tr>
            )}
            <tr><td style={miniLabel}>− InteGolf platform fee ({pctStr(d.fees.platformPct)})</td><td style={{ ...miniAmt, color: '#b91c1c' }}>−{money(d.fees.platformFee)}</td></tr>
            {gateway > 0 && (
              <tr><td style={miniLabel}>− InteGolf gateway fee ({pctStr(d.fees.gatewayPct)})</td><td style={{ ...miniAmt, color: '#b91c1c' }}>−{money(gateway)}</td></tr>
            )}
            {taxWithheld > 0.005 && (
              <tr><td style={miniLabel}>− Tax withheld for remittance ({pctStr(d.tax.rate)})</td><td style={{ ...miniAmt, color: '#b91c1c' }}>−{money(taxWithheld)}</td></tr>
            )}
            {d.parentSplit && (
              <tr><td style={miniLabel}>− Parent markup → {d.parentSplit.parentName || `account ${d.parentSplit.parentAccountId}`}</td><td style={{ ...miniAmt, color: '#b91c1c' }}>−{money(d.parentSplit.parentMarkup)}</td></tr>
            )}
            {d.booking.RefundedAmount > 0 && (
              <tr><td style={miniLabel}>− Refunded to customer</td><td style={{ ...miniAmt, color: '#b91c1c' }}>−{money(d.booking.RefundedAmount)}</td></tr>
            )}
            <tr style={{ borderTop: '2px solid #cbd5e1' }}>
              <td style={{ ...miniLabel, fontWeight: 700 }}>Net owed to reseller (derived)</td>
              <td style={{ ...miniAmt, fontWeight: 700 }}>{money(d.net.derived)} {cur}</td>
            </tr>
            {d.net.recorded != null && (
              <tr>
                <td style={{ ...miniLabel, color: '#64748b' }}>Recorded net on booking</td>
                <td style={{ ...miniAmt }}>
                  {money(d.net.recorded)}{' '}
                  <span style={netMatch ? badgeOk : badgeWarn}>{netMatch ? 'matches' : 'differs'}</span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>
          Fee rates from {d.fees.source === 'snapshot' ? 'the booking-time snapshot' : 'current fee settings'}
          {d.booking.PaymentMode ? ` · Payment mode ${d.booking.PaymentMode}` : ''}
        </div>
        {taxAmt > 0.005 && (
          <div style={{ fontSize: 12, color: '#3730a3', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 6, padding: '8px 10px', marginTop: 8 }}>
            <strong>Tax:</strong> ~{money(taxAmt)} of the total is tax ({pctStr(d.tax.rate)}, from {d.tax.source === 'snapshot' ? 'the booking-time snapshot' : 'current rates'}).{' '}
            {d.tax.remitter === 'integolf'
              ? <>Mode A — InteGolf is merchant of record, so this is <strong>withheld from the payout above</strong> and remitted by InteGolf (a <code>mode_a_tax</code> ledger line once settled).</>
              : <>Mode B — collected through the reseller's own gateway, so it <strong>stays with the reseller</strong>, who is responsible for remitting it.</>}
          </div>
        )}
        {d.net.recorded != null && !netMatch && (
          <div style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 10px', marginTop: 8 }}>
            Derived net differs from the recorded net by {money(Math.abs(d.net.delta))}.{' '}
            {d.net.deltaFromGatewayAndTax
              ? `The stored net was calculated before the gateway fee${taxWithheld > 0.005 ? ' and tax withholding' : ''} applied${gateway > 0.005 ? ` (gateway ${money(gateway)}` : ''}${gateway > 0.005 && taxWithheld > 0.005 ? ` + tax ${money(taxWithheld)})` : gateway > 0.005 ? ')' : ''}, so it over-counts by that amount.`
              : 'This booking was likely settled under a different fee configuration than the current settings.'}
          </div>
        )}
      </div>

      {/* ── Settlement ledger (if present) ── */}
      {d.ledger && (
        <div style={{ ...panel, gridColumn: '1 / -1' }}>
          <div style={panelHead}>
            Settlement ledger
            {d.ledger.reconciliation && (
              <span style={d.ledger.reconciliation.balanced ? badgeOk : badgeWarn}>
                {d.ledger.reconciliation.balanced ? 'reconciled' : `off by ${money(Math.abs(d.ledger.reconciliation.diff))}`}
              </span>
            )}
          </div>
          <table style={miniTbl}>
            <thead>
              <tr>
                <td style={{ ...miniLabel, color: '#94a3b8', fontSize: 11 }}>Entry</td>
                <td style={{ ...miniLabel, color: '#94a3b8', fontSize: 11 }}>Account</td>
                <td style={{ ...miniAmt, color: '#94a3b8', fontSize: 11 }}>Net</td>
                <td style={{ ...miniAmt, color: '#94a3b8', fontSize: 11 }}>Tax</td>
                <td style={{ ...miniAmt, color: '#94a3b8', fontSize: 11 }}>Amount</td>
              </tr>
            </thead>
            <tbody>
              {d.ledger.rows.map((r, i) => (
                <tr key={i}>
                  <td style={{ ...miniLabel, fontFamily: 'monospace', fontSize: 12 }}>{r.EntryType}</td>
                  <td style={{ ...miniLabel, color: '#64748b', fontSize: 12 }}>{r.AccountName || `acct ${r.ResellerAccount_ID}`}</td>
                  <td style={{ ...miniAmt, color: '#64748b' }}>{r.AmountNet != null ? money(r.AmountNet) : '—'}</td>
                  <td style={{ ...miniAmt, color: '#64748b' }}>{r.AmountTax != null ? money(r.AmountTax) : '—'}</td>
                  <td style={{ ...miniAmt, color: Number(r.Amount) < 0 ? '#b91c1c' : '#16a34a' }}>{money(r.Amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

const panel = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }
const panelHead = { fontSize: 12, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }
const miniTbl = { width: '100%', borderCollapse: 'collapse', fontSize: 13 }
const miniLabel = { padding: '5px 0', textAlign: 'left', color: '#0f172a' }
const miniAmt = { padding: '5px 0', textAlign: 'right', fontFamily: 'monospace', whiteSpace: 'nowrap', paddingLeft: 16 }
const badgeOk = { display: 'inline-block', marginLeft: 8, padding: '1px 8px', background: '#dcfce7', color: '#166534', borderRadius: 999, fontSize: 11, fontWeight: 600 }
const badgeWarn = { display: 'inline-block', marginLeft: 8, padding: '1px 8px', background: '#fef3c7', color: '#92400e', borderRadius: 999, fontSize: 11, fontWeight: 600 }

function Field({ label, children, style }) {
  return (
    <div style={{ ...style }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</label>
      {children}
    </div>
  )
}
const inp = { width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }
const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, marginBottom: 16 }
const empty = { padding: 40, textAlign: 'center', color: '#64748b', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }
const tbl = { width: '100%', borderCollapse: 'collapse', fontSize: 14 }
const tr = { borderBottom: '1px solid #f1f5f9' }
const th = { padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 12, letterSpacing: 0.5, textTransform: 'uppercase', background: '#f8fafc' }
const td = { padding: '12px 16px', verticalAlign: 'middle' }
const tdMono = { ...td, fontFamily: 'monospace', fontSize: 13 }
const primaryBtn = { padding: '10px 16px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
const secondaryBtn = { padding: '8px 14px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, cursor: 'pointer' }
